/**
 * MC_DECL_V2_SUP gate — the chain a late supplier assignment fires.
 *
 * `assign_supplier` (backend) only sets the FK; it deliberately does not
 * re-run article matching. So once the mandatory gate's `SupplierAssignControl`
 * reports a successful assign (`onAssigned`), THIS hook is what actually gets
 * the declaration re-evaluated against the new supplier's articles: it POSTs
 * `/reextract` (existing endpoint, existing hook — `useReextractDeclaration`,
 * the same one `DeclarationReextractDialog` already uses from the list). That
 * flips the declaration back to `pending`, and the ordinary extraction
 * pipeline picks it up from there — no new backend surface.
 *
 * THE LATCH. `reextract`'s own POST resolves in milliseconds; the actual
 * recompute happens later, out of band, over the SQS-driven pipeline. Between
 * "assign succeeded" and "the pipeline has actually re-published", the
 * declaration's OWN queries (extraction/statements/scope) are mid-flight and
 * can settle in either order — `extractionQ` alone can refetch first and
 * report the newly-bound supplier while `statementsQ` still holds the
 * PRE-reextract cards for a beat, which would flash stale statements right
 * where the gate just stood. `recomputing` is a local latch that outlives
 * that race: it goes true the instant reextract is fired and only releases
 * once `awaitingExtraction` (the declaration's own pending/processing signal,
 * already computed one level up in `DeclarationReviewPage`) has been OBSERVED
 * true and then false again — the same true-then-false latch shape
 * `DeclarationReviewPage`'s own `wasAwaiting` ref already uses for the
 * post-extraction hydrate. A bounded fallback timer releases it regardless
 * if that round-trip is never observed (a pipeline fast enough to finish
 * between two polls) so the caller can never get stuck mid-spinner.
 *
 * 409s are read the same way `SupplierAssignControl` reads its own conflict
 * codes: `HAS_REVIEW_DECISIONS`/`ALREADY_PROCESSING`/`ALREADY_APPROVED` are
 * not failures from the reviewer's point of view — the supplier IS assigned
 * now, only the recompute did not run — so they get a calm `toast.warning`
 * and the latch releases immediately, falling through to the ordinary
 * (supplier-bound) review rather than re-showing the gate or spinning
 * forever.
 */
import { toast } from "@/components/common/Toast/Toast"
import { isPermissionDenied } from "@/hooks/useGuardedMutation"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useCallback, useEffect, useRef, useState } from "react"
import { getApiErrorCode, toastApiError } from "../../../../../api/apiError"
import {
  useAssignDeclarationSupplier,
  useReextractDeclaration,
} from "../../../../../api/declarationsApi"

/** How long to wait for the true-then-false `awaitingExtraction` round-trip
 *  before releasing the latch anyway — long enough for a real pipeline run,
 *  short enough that a stuck spinner never reads as the page having hung. */
const RECOMPUTE_FALLBACK_MS = 30_000

/** Reextract's own 409s that mean "the supplier is assigned, but the
 *  recompute did not run" — never a failure to surface as an error. */
const CALM_REEXTRACT_CODES: Partial<Record<string, TranslationKeys>> = {
  ALREADY_PROCESSING: "MC_DECL_V2_SUP_REEXTRACT_ALREADY_PROCESSING",
  HAS_REVIEW_DECISIONS: "MC_DECL_V2_SUP_REEXTRACT_HAS_DECISIONS",
  ALREADY_APPROVED: "MC_DECL_V2_SUP_REEXTRACT_ALREADY_APPROVED",
  RECOMPUTE_IN_PROGRESS: "MC_DECL_V2_SUP_REEXTRACT_RECOMPUTE_RUNNING",
}

export interface SupplierGateAssign {
  /** True from the instant reextract is fired until the pipeline's own
   *  pending/processing round-trip has been observed, or a bounded fallback
   *  releases it — see the module doc's "THE LATCH". */
  recomputing: boolean
  /** Pass as `SupplierAssignControl`'s `onAssigned` — fires the
   *  re-extraction that recomputes scope/verdicts against the new supplier's
   *  articles. */
  handleAssigned: () => void
  /** Assign a supplier AND run the post-assign chain, in one call.
   *
   *  For callers that have a supplier id but no picker — Turnus Memory's suggestion is
   *  the one today. `handleAssigned` alone is not enough for them: the picker performs
   *  the assignment itself and only reports it, so a caller without a picker would have
   *  to duplicate the PUT. Keeping it here means there is still exactly one place a
   *  supplier gets bound from this screen.
   *
   *  Rejects on failure so the caller can decline to record anything. */
  assignAndRecompute: (supplierId: string) => Promise<void>
}

export function useSupplierGateAssign(
  declId: string,
  awaitingExtraction: boolean,
): SupplierGateAssign {
  const { translate } = useTranslation()
  const reextract = useReextractDeclaration()
  const assign = useAssignDeclarationSupplier()
  const [recomputing, setRecomputing] = useState(false)
  // True-then-false latch on the SAME signal `DeclarationReviewPage`'s own
  // `wasAwaiting` ref reads — set once `awaitingExtraction` is seen true
  // while recomputing, cleared (and the latch released) once it is seen
  // false again afterwards.
  const sawAwaiting = useRef(false)
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Latest-value refs for `reextract` (a fresh object every render — it
  // comes out of `useGuardedMutation`'s own object spread) and `translate`
  // (re-created only on language change). `handleAssigned` reads through
  // these instead of closing over the values directly, so it never fires a
  // stale `mutate`/`translate` while still keeping its own identity stable
  // across renders — the callback is threaded down as a prop to
  // `SupplierAssignControl` and only ever invoked from a user click, so
  // recreating it on every render/language change would buy nothing.
  // Updated in an effect (not during render) per React's rule against
  // mutating refs while rendering.
  const reextractRef = useRef(reextract)
  const assignRef = useRef(assign)
  const translateRef = useRef(translate)
  useEffect(() => {
    reextractRef.current = reextract
    assignRef.current = assign
    translateRef.current = translate
  })

  const clearFallback = useCallback(() => {
    if (fallbackTimer.current) {
      clearTimeout(fallbackTimer.current)
      fallbackTimer.current = null
    }
  }, [])

  const release = useCallback(() => {
    sawAwaiting.current = false
    clearFallback()
    setRecomputing(false)
  }, [clearFallback])

  useEffect(() => {
    if (!recomputing) return
    if (awaitingExtraction) {
      // The real signal has been observed — the bounded fallback was only
      // ever a guard against never seeing it at all.
      sawAwaiting.current = true
      clearFallback()
      return
    }
    if (sawAwaiting.current) release()
    // `clearFallback`/`release` are both `useCallback`s with an empty (or
    // all-stable) dependency chain, so listing them here does not make this
    // effect re-run on every render — it just satisfies exhaustive-deps
    // honestly instead of suppressing it.
  }, [awaitingExtraction, recomputing, clearFallback, release])

  // Unmount safety only — a fresh mount (declaration switch) starts its own
  // hook instance with its own timer.
  useEffect(() => clearFallback, [clearFallback])

  const handleAssigned = useCallback(() => {
    setRecomputing(true)
    sawAwaiting.current = false
    clearFallback()
    fallbackTimer.current = setTimeout(release, RECOMPUTE_FALLBACK_MS)
    reextractRef.current.mutate(declId, {
      onError: (error) => {
        const code = getApiErrorCode(error)
        const calmKey = code ? CALM_REEXTRACT_CODES[code] : undefined
        if (calmKey) {
          toast.warning(translateRef.current(calmKey))
        } else {
          toast.error(translateRef.current("MC_DECL_V2_SUP_REEXTRACT_FAILED"))
        }
        release()
      },
    })
    // `reextractRef`/`translateRef` are refs, not reactive values, so they
    // are intentionally not listed — that's the point of the latest-value
    // ref pattern above. `clearFallback`/`release` are stable callbacks.
  }, [declId, clearFallback, release])

  const assignAndRecompute = useCallback(
    async (supplierId: string) => {
      try {
        await assignRef.current.mutateAsync({ declId, supplierId })
      } catch (error) {
        // A permission short-circuit is silent by contract: `useGuardedMutation` throws
        // before any request when `canRun` is false, and `toastApiError` only suppresses
        // >=500 — a PermissionDeniedError carries no status, so it would toast "could not
        // be assigned", which names the wrong cause for a control that should not have
        // been reachable. (`SupplierAssignControl` has the same gap on its own path;
        // widening this PR to change the picker would be a different review.)
        if (!isPermissionDenied(error)) {
          // Otherwise the same message the picker shows for the same failure — an
          // assignment that came from a suggestion is still an assignment, and should not
          // read as a different kind of problem.
          toastApiError(
            error,
            translateRef.current,
            "MC_DECL_V2_SUP_ASSIGN_FAILED",
          )
        }
        // Re-thrown either way, so the caller declines to record an accept for a write
        // that did not happen.
        throw error
      }
      handleAssigned()
    },
    // `assignRef`/`translateRef` are refs, per the latest-value pattern above.
    [declId, handleAssigned],
  )

  return { recomputing, handleAssigned, assignAndRecompute }
}
